import { arcgisServicesLandFeatureUrl } from "../../configs/arcgisServices";
import Request from "@arcgis/core/request";

export const requestOptions = (
  body?: any,
  returnCountOnly?: boolean,
  returnGeometry?: boolean,
  spatialRel?: string,
  geometry?: any,
  inSR?: string,
  outSR?: string,
  where?: string,
  geometryType?: string
) => {
  return {
    responseType: "json",
    body: body,
    query: {
      f: "json",
      returnCountOnly: returnCountOnly,
      outFields: "*",
      where: where,
      returnGeometry: returnGeometry,
      spatialRel: spatialRel,
      geometry: geometry,
      inSR: inSR,
      outSR: outSR,
      geometryType: geometryType,
    },
  };
};

export const pictureSymbolFunction = (
  file: any,
  width: String,
  height: String,
) => {
  return {
    type: "picture-marker",
    url: file,
    width: width,
    height: height,
    contentType: 'image/svg+xml',
    angle: 0,
  };
};


export const selectedSymbol = {
  type: "simple-fill",
  color: "rgba(90, 232, 181, 0.3)",
  style: "solid",
  outline: {
    color: "white",
    width: 1,
  },
};


export const favoriteLandSymbol = {
  type: "simple-marker",
  style: "circle",
  color: "red",
  size: "50px",
  outline: {
    color: [255, 255, 0],
    width: 2
  }
};

export const selectedSymbolElshamalya = {
  type: "simple-fill",
  color: "rgba(119, 218, 236, 0.3)",
  style: "solid",
  outline: {
    color: "white",
    width: 1,
  },
};


export const featureGeometry = (type: any, features: any, wKid: any) => {
  let obj: any = {
    type: type,
    spatialReference: {
      wkid: wKid,
    }
  };
  switch (type) {
    case 'polyline':
      obj['paths'] = features;
      break;
    case 'polygon':
      obj['rings'] = features;
  }
  return obj;
}

/**
 * Calculates the centroid (center point) of a polygon from its rings geometry
 * @param geometry - The geometry object containing rings array
 * @returns Object with x, y coordinates and longitude, latitude
 */
export const calculatePolygonCentroid = (geometry: { rings: number[][][] }) => {
  if (!geometry || !geometry.rings || geometry.rings.length === 0) {
    throw new Error('Invalid geometry: rings array is required');
  }

  // Get the first ring (outer boundary of the polygon)
  const ring = geometry.rings[0];
  
  if (!ring || ring.length < 3) {
    throw new Error('Invalid polygon: at least 3 points required');
  }

  let area = 0;
  let centroidX = 0;
  let centroidY = 0;

  // Calculate centroid using the shoelace formula (also known as surveyor's formula)
  for (let i = 0; i < ring.length - 1; i++) {
    const currentPoint = ring[i];
    const nextPoint = ring[i + 1];
    
    const crossProduct = (currentPoint[0] * nextPoint[1]) - (nextPoint[0] * currentPoint[1]);
    area += crossProduct;
    
    centroidX += (currentPoint[0] + nextPoint[0]) * crossProduct;
    centroidY += (currentPoint[1] + nextPoint[1]) * crossProduct;
  }

  // Complete the polygon by connecting last point to first
  const lastPoint = ring[ring.length - 1];
  const firstPoint = ring[0];
  const crossProduct = (lastPoint[0] * firstPoint[1]) - (firstPoint[0] * lastPoint[1]);
  area += crossProduct;
  centroidX += (lastPoint[0] + firstPoint[0]) * crossProduct;
  centroidY += (lastPoint[1] + firstPoint[1]) * crossProduct;

  // Finalize calculations
  area = area / 2;
  centroidX = centroidX / (6 * area);
  centroidY = centroidY / (6 * area);

  return {
    x: centroidX,
    y: centroidY,
    longitude: centroidX,
    latitude: centroidY
  };
};

/**
 * Gets the center point and centroid coordinates from polygon geometry
 * @param geometry - The geometry object containing rings array
 * @returns Object with centroid coordinates
 */
export const getPolygonCenterPoint = (geometry: { rings: number[][][] }) => {
  try {
    return calculatePolygonCentroid(geometry);
  } catch (error) {
    console.error('Error calculating polygon centroid:', error);
    return null;
  }
};

// Generic fetch function for any ArcGIS API request
export const getLandAdsByCoords = async (
  x: number,
  y: number,
  where: string = "Parcel_Status IS NOT NULL",
  outFields: string = "*",
  spatialRel: string = "esriSpatialRelIntersects",
  geometryType: string = "esriGeometryPoint",
  inSR: string = "102100",
  outSR: string = "102100"
) => {
  const queryOptions: any = {
    responseType: "json",
    query: {
      f: "json",
      geometry: {
        x: x,
        y: y
      },
      outFields: outFields,
      spatialRel: spatialRel,
      where: where,
      geometryType: geometryType,
      inSR: inSR,
      outSR: outSR,
    },
  };

  try {
    const response = await Request(arcgisServicesLandFeatureUrl + "1/query", queryOptions);
    if (response?.data?.features?.length > 0) {
      return response.data.features;
    }
    return [];
  } catch (error) {
    console.error("Error fetching data from ArcGIS:", error);
    return null;
  }
};

const development: boolean = !process.env.NODE_ENV || process.env.NODE_ENV === 'development';
export default function isDev(): boolean {
  return development;
}